import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, Calendar, MessageSquare, MapPin, Plane, BookOpen, LogOut, X } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../contexts/AuthContext";
import { NAV, DRAWER } from "../constants/testIds";

// New order per spec — Home, Ask Murshid, Calendar, Geolocation.
// "Coming Soon" divider then Hajj & Umrah and Messaging (muted, non-clickable).
const mainItems = [
  { id: "home",        label: "Home",         icon: Home,     path: "/dashboard",   testId: DRAWER.itemHome },
  { id: "murshid",     label: "Ask Murshid",  icon: BookOpen, path: "/murshid",     testId: DRAWER.itemMurshid },
  { id: "calendar",    label: "Calendar",     icon: Calendar, path: "/calendar",    testId: DRAWER.itemCalendar },
  { id: "geolocation", label: "Geolocation",  icon: MapPin,   path: "/geolocation", testId: DRAWER.itemGeo },
];

const comingSoonItems = [
  { id: "hajj",      label: "Hajj & Umrah", icon: Plane,         path: "/hajj",      testId: DRAWER.itemHajj },
  { id: "messaging", label: "Messaging",    icon: MessageSquare, path: "/messaging", testId: DRAWER.itemMessaging },
];

const dividerLabelStyle = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "var(--text-muted)",
  padding: "12px 16px 4px",
  fontWeight: 500,
};

const soonBadgeStyle = {
  fontSize: 9,
  background: "#1a5c1a",
  color: "#CEF0CE",
  borderRadius: 20,
  padding: "1px 6px",
  marginLeft: "auto",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
};

export default function Drawer({ open, onClose }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const { logout } = useAuth();
  const go = (path) => { onClose(); navigate(path); };
  const isActive = (path) => loc.pathname === path;

  if (!open) return null;

  return (
    <>
      <div
        data-testid={NAV.drawerOverlay}
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(10, 46, 10, 0.55)",
          backdropFilter: "blur(2px)", zIndex: 1100,
        }}
      />
      <aside
        data-testid={NAV.drawer}
        className="dnya-drawer-anim"
        style={{
          position: "fixed", top: 0, left: 0, bottom: 0,
          width: "min(85vw, 280px)",
          background: "var(--dnya-bg)",
          borderRight: `1px solid var(--dnya-mosaic-1)`,
          zIndex: 1200,
          display: "flex", flexDirection: "column",
          color: "var(--dnya-text)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", padding: "16px 16px", gap: 10, borderBottom: `1px solid var(--dnya-mosaic-1)` }}>
          <Logo size={40} />
          <div className="font-dnya" style={{ fontSize: 22, color: "var(--dnya-high-1)" }}>Dnya</div>
          <button
            data-testid={NAV.drawerClose}
            onClick={onClose}
            aria-label="Close menu"
            style={{ marginLeft: "auto", background: "transparent", border: 0, color: "var(--dnya-high-1)", cursor: "pointer", padding: 8, minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <X size={20} />
          </button>
        </div>

        <nav style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {mainItems.map((it) => {
            const Icon = it.icon;
            const active = isActive(it.path);
            return (
              <button
                key={it.id}
                data-testid={it.testId}
                onClick={() => go(it.path)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12,
                  padding: "14px 18px",
                  minHeight: 48,
                  background: active ? "var(--dnya-mosaic-1)" : "transparent",
                  color: active ? "#ffffff" : "var(--dnya-text)",
                  border: 0, textAlign: "left", cursor: "pointer",
                  fontSize: 14, fontWeight: 500,
                  transition: "background 150ms ease",
                }}
              >
                <Icon size={18} />
                <span style={{ flex: 1 }}>{it.label}</span>
              </button>
            );
          })}

          <div data-testid={DRAWER.comingSoonLabel} style={dividerLabelStyle}>Coming Soon</div>

          {comingSoonItems.map((it) => {
            const Icon = it.icon;
            return (
              <button
                key={it.id}
                data-testid={it.testId}
                aria-disabled="true"
                tabIndex={-1}
                onClick={(e) => e.preventDefault()}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12,
                  padding: "14px 18px",
                  minHeight: 48,
                  background: "transparent",
                  color: "var(--dnya-text)",
                  border: 0, textAlign: "left",
                  fontSize: 14, fontWeight: 500,
                  opacity: 0.5,
                  cursor: "not-allowed",
                  pointerEvents: "none",
                }}
              >
                <Icon size={18} />
                <span style={{ flex: 1 }}>{it.label}</span>
                <span data-testid={`drawer-soon-${it.id}`} style={soonBadgeStyle}>Soon</span>
              </button>
            );
          })}
        </nav>

        <div style={{ borderTop: `1px solid var(--dnya-mosaic-1)`, padding: "8px 0" }}>
          <button
            data-testid={DRAWER.itemLogout}
            onClick={() => { onClose(); logout(); }}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", minHeight: 44, background: "transparent", color: "var(--dnya-high-1)", border: 0, cursor: "pointer", fontSize: 14, fontWeight: 600 }}
          >
            <LogOut size={18} /> Logout
          </button>
        </div>
      </aside>
    </>
  );
}
